import React, { FC, useCallback } from 'react';
import { Approval as ApprovalType } from 'api/approval';
import { DocumentId, ProjectId } from 'api/models';
import { Approvals } from 'components/organisms/approvals';
import {
  useCreateDocumentApprovalMutation,
  useDeleteDocumentApprovalMutation,
  useUpdateDocumentApprovalMutation,
} from 'queries/document-approvals';

interface Props {
  documentId: number;
  projectId: number;
  isPrivate: boolean;
  newData?: Partial<ApprovalType>;
  data: Array<ApprovalType>;
  onNewApprovalCancel: () => void;
  onNewApprovalSubmit: () => void;
}

export const DocumentApprovals: FC<Props> = ({
  data: approvals = [],
  newData,
  documentId,
  projectId,
  isPrivate,
  onNewApprovalCancel,
  onNewApprovalSubmit,
}) => {
  // New approval actions.
  const { mutate: newApprovalMuatate, isLoading: isNewApprovalActionLoading } =
    useCreateDocumentApprovalMutation(
      DocumentId(documentId),
      ProjectId(projectId),
    );
  const handleNewApprovalSubmit = useCallback(
    (args) => {
      newApprovalMuatate(args, {
        onSuccess: () => {
          if (onNewApprovalSubmit) {
            onNewApprovalSubmit();
          }
        },
      });
    },
    [newApprovalMuatate, onNewApprovalSubmit],
  );

  // Approval item change actions. (Approve/Reject)
  const { mutate, isLoading } = useUpdateDocumentApprovalMutation(
    DocumentId(documentId),
    ProjectId(projectId),
  );
  const handleApprovalChange = useCallback(
    (payload, options) => {
      mutate(payload, options);
    },
    [mutate],
  );

  // Approval item change actions. Delete
  const { mutateAsync } = useDeleteDocumentApprovalMutation(
    DocumentId(documentId),
    ProjectId(projectId),
  );
  const handleApprovalDelete = useCallback(
    (id) => {
      return mutateAsync(id);
    },
    [mutateAsync],
  );

  return (
    <Approvals
      onNewApprovalCancel={onNewApprovalCancel}
      onNewApprovalSubmit={handleNewApprovalSubmit}
      newData={newData}
      isPrivate={isPrivate}
      data={approvals}
      projectId={projectId}
      isNewApprovalActionLoading={isNewApprovalActionLoading}
      isApprovalItemActionLoading={isLoading}
      onApprovalItemDelete={handleApprovalDelete}
      onApprovalItemActionSubmit={handleApprovalChange}
      isFromDocument
    />
  );
};

export const MemoizedDocumentApprovals = React.memo(DocumentApprovals);
